import React, { useMemo, useState } from 'react';
import { CalculationResult } from '../utils/roiCalculations';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsSummaryProps {
  results: CalculationResult;
  halfSales?: boolean;
  doubleAttrition?: boolean;
  timeframe?: 3 | 6 | 12;
  onOptimizeForTarget?: (target: number, months: 3 | 6 | 12) => void;
  onUndoOptimize?: () => void;
  canUndoOptimize?: boolean;
}
export function ResultsSummary({
  results,
  halfSales = false,
  doubleAttrition = false,
  timeframe = 12,
  onOptimizeForTarget,
  onUndoOptimize,
  canUndoOptimize = false
}: ResultsSummaryProps) {
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetValue, setTargetValue] = useState('');
  const calculatedTotals = useMemo(() => {
    const lastRow = results.rows[timeframe];
    if (!lastRow) {
      return {
        totalGrossRevenue: 0,
        totalGrossProfit: 0,
        totalNetProfit: 0,
        roi: 0
      };
    }
    let cumGrossRevenue = 0;
    let cumGrossProfit = 0;
    let cumTotalCost = 0;
    for (let i = 0; i <= timeframe; i++) {
      cumGrossRevenue += results.rows[i].grossRevenue;
      cumGrossProfit += results.rows[i].grossProfit;
      cumTotalCost += results.rows[i].totalCost;
    }
    if (halfSales) {
      cumGrossRevenue = cumGrossRevenue * 0.5;
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    if (doubleAttrition) {
      cumGrossRevenue = cumGrossRevenue * 0.5;
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    const roi = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
    const totalNetProfit = cumGrossProfit - cumTotalCost;
    return {
      totalGrossRevenue: cumGrossRevenue,
      totalGrossProfit: cumGrossProfit,
      totalNetProfit,
      roi
    };
  }, [results.rows, halfSales, doubleAttrition, timeframe]);
  const roiClass = calculatedTotals.roi >= 0 ? 'text-green-600' : 'text-red-600';
  const startEditingTarget = () => {
    setTargetValue(String(Math.round(calculatedTotals.totalGrossRevenue)));
    setEditingTarget(true);
  };
  const commitTarget = () => {
    const parsed = parseFloat(targetValue);
    setEditingTarget(false);
    if (!isNaN(parsed) && parsed > 0 && onOptimizeForTarget) {
      onOptimizeForTarget(parsed, timeframe);
    }
  };
  const handleTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitTarget();
    } else if (e.key === 'Escape') {
      setEditingTarget(false);
    }
  };
  const canOptimize = !!onOptimizeForTarget;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6" data-id="element-485" data-mp="2au1i32u5xw5w">
      {/* Hires / Month */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-486" data-mp="2ojv6xl5o4gfm">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-487" data-mp="2kit6x23xg0cl">
          Hires / Month (estimated)
        </div>
        <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-488" data-mp="2yk5il1c21fso">
          {results.hiresPerMonth.toLocaleString()} / mo
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-489" data-mp="2thwp83v9sbq1">
          Launched: {results.launchedPerMonth.toFixed(1)} / mo
        </div>
      </div>

      {/* Gross Revenue - click to set a target and auto-optimize campaigns */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-490" data-mp="337n4qfzknyby">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-491" data-mp="339pwb4gdhzis">
          {timeframe}-Month Gross Revenue
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-492" data-mp="2uea1ak3vtebm">
              (Scenario Active)
            </span>}
        </div>
        {editingTarget ? <div className="relative" data-id="element-493" data-mp="2khnkc6eof7ev">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg" data-id="element-494" data-mp="2kiovsbzltvx4">
              $
            </span>
            <input type="number" min="0" step="10000" value={targetValue} onChange={e => setTargetValue(e.target.value)} onKeyDown={handleTargetKeyDown} onBlur={commitTarget} autoFocus className="w-full pl-6 pr-2 py-1 text-2xl md:text-3xl font-bold text-navy border-2 border-amber rounded outline-none bg-white" data-id="element-495" data-mp="2bgfkhjwihyio" />
          </div> : <div onClick={canOptimize ? startEditingTarget : undefined} className={`text-2xl md:text-3xl font-bold text-navy ${canOptimize ? 'cursor-pointer hover:text-amber transition-colors underline decoration-dotted decoration-gray-400 underline-offset-4' : ''}`} title={canOptimize ? 'Click to set a target — campaigns will auto-optimize' : undefined} data-id="element-496" data-mp="1ymolt3fwf93q">
            {fmtMoney(calculatedTotals.totalGrossRevenue)}
          </div>}
        <div className="text-sm text-gray-600 mt-2 flex items-center gap-2" data-id="element-497" data-mp="3a4g66zwmx3ja">
          {canOptimize ? <span data-id="element-498" data-mp="3566e4y67ij1w">Click to set a {timeframe}-mo target</span> : <span data-id="element-499" data-mp="3dmnj2k2lz4a2">Cumulative top-line revenue</span>}
          {canUndoOptimize && onUndoOptimize && <button type="button" onClick={onUndoOptimize} className="text-amber-600 hover:text-amber font-semibold underline underline-offset-2" data-id="element-500" data-mp="3hmnurwybp1df">
              Undo
            </button>}
        </div>
      </div>

      {/* Gross Profit */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-501" data-mp="2uob6eochnc9q">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-502" data-mp="2yobnkgqz21kh">
          {timeframe}-Month Gross Profit
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-503" data-mp="33q0yzxi3wh93">
              (Scenario Active)
            </span>}
        </div>
        <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-504" data-mp="iyiby0r5636a">
          {fmtMoney(calculatedTotals.totalGrossProfit)}
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-505" data-mp="nuz18ya2unky">
          GP / Sale: {fmtMoney(results.gpPerSale)}
        </div>
      </div>

      {/* ROI */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-506" data-mp="3pfh7ghu3cwf9">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-507" data-mp="86u3vje2nh9s">
          {timeframe}-Month ROI
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-508" data-mp="392eox4ta99a">
              (Scenario Active)
            </span>}
        </div>
        <div className={`text-2xl md:text-3xl font-bold ${roiClass}`} data-id="element-509" data-mp="36ohnxw5d8etd">
          {Math.round(calculatedTotals.roi)}%
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-510" data-mp="3ldm4l8wiiazd">
          Net Profit: {fmtMoney(calculatedTotals.totalNetProfit)}
        </div>
      </div>
    </div>;
}